/* !
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	AfterViewInit,
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output
} from '@angular/core';
import {
	AbstractControl
} from '@angular/forms';
import {
	DynamicWizardComponent
} from '@dynamicComponents/dynamic-wizard/dynamic-wizard.component';
import {
	InsuranceConstants
} from '@insurance/constants/insurance-constants';
import {
	InsuranceHelper
} from '@insurance/helpers/insurance.helper';
import {
	FormlyFieldConfig
} from '@ngx-formly/core/lib/models/fieldconfig';
import {
	FormlyConstants
} from '@shared/constants/formly.constants';
import {
	AnyHelper
} from '@shared/helpers/any.helper';
import {
	ObjectHelper
} from '@shared/helpers/object.helper';
import {
	IDynamicComponentContext
} from '@shared/interfaces/application-objects/dynamic-component-context.interface';
import {
	IDynamicComponent
} from '@shared/interfaces/application-objects/dynamic-component.interface';
import {
	IWizardContext
} from '@shared/interfaces/dynamic-interfaces/wizard-context.interface';

/* eslint-enable max-len */

@Component({
	selector: 'payment-allocations',
	templateUrl: './payment-allocations.component.html',
})

/**
 * Component class to manage payment allocations.
 *
 * @export
 * @class PaymentAllocationsComponent
 * @implements {OnInit}
 * @implements {IDynamicComponent<DynamicWizardComponent, IWizardContext>}
 */
export class PaymentAllocationsComponent implements OnInit, AfterViewInit,
	IDynamicComponent<DynamicWizardComponent, IWizardContext>
{
	/**
	 * Gets or sets the context of this dynamic component that will be set
	 * during initialization. The source is the content component and
	 * the data will be associated data that we desire to pass explicitly.
	 *
	 * @type {IDynamicComponentContext<
	 * 	DynamicWizardComponent,
	 * 	IWizardContext>}
	 * @memberof PaymentAllocationsComponent
	 */
	@Input() public context: IDynamicComponentContext<
		DynamicWizardComponent,
		IWizardContext>;

	/**
	 * Gets or sets the payment type to determine the type of payment
	 * allocations to be displayed. This is required to load the correct
	 * payment reference options based on the transaction type.
	 *
	 * @type {string}
	 * @memberof PaymentAllocationsComponent
	 */
	@Input() public paymentType: string;

	/**
	 * Gets or sets a boolean value indicating whether this component
	 * allows to enter negative amounts on payment allocations.
	 */
	@Input() public allowNegativeAmounts: boolean = false;

	/**
	 * Gets or sets the payment allocation settings retrieved from
	 * the product associated to the policy term.
	 *
	 * @type {any}
	 * @memberof PaymentAllocationsComponent
	 */
	@Input() public paymentAllocationSettings: {
		enabled: boolean;
		policy: {
			label: string;
			path: string;
			itemDescription: string;
			defaultAmountPath: string;
			invoiceAmountPath: string;
		};
		commission: {
			label: string;
			path: string;
			itemDescription: string;
			defaultAmountPaths: any[];
		};
	};

	/**
	 * Gets or sets the formly field configuration for the transaction amount
	 * field that is displayed in the parent component. This is required
	 * to set the transaction amount based on the selected transaction.
	 *
	 * @type {FormlyFieldConfig}
	 * @memberof PaymentAllocationsComponent
	 */
	@Input() public transactionAmountField: FormlyFieldConfig;

	/**
	 * Gets or sets the formly field configuration for the invoice amount
	 * field that is displayed in the parent component. This is required
	 * to set the invoice amount based on the selected transaction.
	 *
	 * @type {FormlyFieldConfig}
	 * @memberof PaymentAllocationsComponent
	 */
	@Input() public invoiceAmountField: FormlyFieldConfig;

	/**
	 * Gets or sets the formly field configuration for the allocation amount
	 * field that is displayed in the parent component. This is required
	 * to keep the allocation amount in sync with the sum of amounts
	 * from each allocation item.
	 *
	 * @type {FormlyFieldConfig}
	 * @memberof PaymentAllocationsComponent
	 */
	@Input() public allocationAmountField: FormlyFieldConfig;

	/**
	 * Gets or sets the validity changed event emitter. This will notify
	 * listening components of a change in the display form validity.
	 *
	 * @type {EventEmitter<boolean>}
	 * @memberof PaymentAllocationsComponent
	 */
	@Output() public validityChanged: EventEmitter<boolean> =
		new EventEmitter<boolean>();

	/**
	 * Gets or sets a value indicating whether
	 * the component is in a loading state.
	 *
	 * @type {boolean}
	 * @memberof PaymentAllocationsComponent
	 */
	public loading: boolean = true;

	/**
	 * Gets or sets the formly layout holding the allocation controls.
	 *
	 * @type {FormlyFieldConfig[]}
	 * @memberof PaymentAllocationsComponent
	 */
	public dynamicFormlyLayout: FormlyFieldConfig[];

	/**
	 * Gets or sets the payment reference options to be displayed in the
	 * reference select field.
	 *
	 * @type {any[]}
	 * @memberof PaymentAllocationsComponent
	 */
	public paymentReferenceOptions: any[] = [];

	/**
	 * Gets or sets the current commission transaction associated
	 * with the commission payment.
	 */
	public commissionTransaction: any;

	/**
	 * Gets a value indicating whether the current component is valid.
	 *
	 * @type {boolean}
	 * @memberof PaymentAllocationsComponent
	 */
	public get valid(): boolean
	{
		return this.dynamicFormlyLayout[0].formControl.valid;
	}

	/**
	 * Gets or sets the context active menu item current data
	 * collected on the dynamic wizard steps.
	 *
	 * @type {any}
	 * @memberof PaymentAllocationsComponent
	 */
	public get currentData(): any
	{
		return this.context.source.activeMenuItem.currentData.data;
	}

	/**
	 * Gets a value indicating whether payment allocations are enabled.
	 *
	 * @type {boolean}
	 * @memberof PaymentAllocationsComponent
	 */
	public get paymentAllocationsEnabled(): boolean
	{
		return this.paymentAllocationSettings?.enabled ?? false;
	}

	/**
	 * Gets the payment allocations repeater field configuration.
	 *
	 * @type {FormlyFieldConfig}
	 * @memberof PaymentAllocationsComponent
	 */
	public get allocationsRepeaterConfig(): FormlyFieldConfig
	{
		return this.dynamicFormlyLayout[0];
	}

	/**
	 * Gets the transaction instance selected in the parent component.
	 *
	 * @type {any}
	 * @memberof PaymentAllocationsComponent
	 */
	public get transaction(): any
	{
		return this.currentTransaction;
	}

	/**
	 * Sets the transaction instance selected in the parent component.
	 * This will trigger the loading of payment reference options
	 * and set the transaction amount in the form.
	 *
	 * @type {any}
	 * @memberof PaymentAllocationsComponent
	 */
	@Input() public set transaction(value: any)
	{
		this.currentTransaction = value;

		if(this.loading)
		{
			return;
		}

		this.refreshTransactionAssociatedControls();
	}

	/**
	 * Sets the auto allocate button field configuration.
	 * This sets the auto allocate function and the
	 * disabled state based on the entered amount.
	 *
	 * @type {FormlyFieldConfig}
	 * @memberof PaymentAllocationsComponent
	 */
	@Input() public set autoAllocateButton(value: FormlyFieldConfig)
	{
		this._autoAllocateButton = value;

		if (AnyHelper.isNull(
			this._autoAllocateButton))
		{
			return;
		}

		this.decorateAutoAllocateButton();
	}

	/**
	 * Sets the amount field configuration. This adds a custom validator
	 * to ensure that the entered amount matches the allocated amount sum.
	 *
	 * @type {FormlyFieldConfig}
	 * @memberof PaymentAllocationsComponent
	 */
	@Input() public set amountField(value: FormlyFieldConfig)
	{
		this._amountField = value;

		if (AnyHelper.isNull(
			this._amountField))
		{
			return;
		}

		this.decorateAmountField();
	}

	/**
	 * Gets or sets the current transaction instance
	 * set from the parent component.
	 *
	 * @type {any}
	 * @memberof PaymentAllocationsComponent
	 */
	private currentTransaction: any;

	/**
	 * Gets or sets the amount field configuration
	 * set from the parent component.
	 *
	 * @type {FormlyFieldConfig}
	 * @memberof PaymentAllocationsComponent
	 */
	private _amountField: FormlyFieldConfig;

	/**
	 * Gets or sets the auto allocate button field configuration
	 * set from the parent component.
	 *
	 * @type {FormlyFieldConfig}
	 * @memberof PaymentAllocationsComponent
	 */
	private _autoAllocateButton: FormlyFieldConfig;

	/**
	 * Initializes the component and creates the dynamic formly layout.
	 *
	 * @memberof PaymentAllocationsComponent
	 */
	public ngOnInit(): void
	{
		this.initializeLayout();
		this.loading = false;
	}

	/**
	 * Lifecycle hook that is called after the component's view
	 * has been fully initialized.
	 * This is used to refresh the controls associated with the transaction
	 * after the view is ready to ensure that all form controls are available.
	 *
	 * @memberof PaymentAllocationsComponent
	 */
	public ngAfterViewInit(): void
	{
		this.refreshTransactionAssociatedControls();
		this.decorateAutoAllocateButton();
		this.decorateAmountField();
	}

	/**
	 * Loads the payment reference options based on the payment type and
	 * the current transaction data.
	 *
	 * @memberof PaymentAllocationsComponent
	 */
	public loadPaymentReferenceOptions(): void
	{
		switch(this.paymentType)
		{
			case InsuranceConstants.paymentAllocationTypes.policy:
				this.paymentReferenceOptions =
					InsuranceHelper
						.loadPaymentReferenceOptions(
							this.currentTransaction,
							this.paymentAllocationSettings);
				break;
			case InsuranceConstants.paymentAllocationTypes.commission:
				this.paymentReferenceOptions =
					InsuranceHelper
						.loadCommissionPaymentReferenceOptions(
							this.currentTransaction,
							this.paymentAllocationSettings,
							this.commissionTransaction
								?.data
								?.metadata
								?.commissionScheduleCalculationIdentifier);
				break;
			default:
				this.paymentReferenceOptions = [];
		}
	}

	/**
	 * Automatically allocates the payment amount to the references.
	 *
	 * @memberof PaymentAllocationsComponent
	 */
	public autoAllocate(): void
	{
		const allocations: any[] =
			this.paymentReferenceOptions
				.map(
					option => ({
						referenceId:
							option.id,
						transactionAmount:
							option.defaultAmount ?? 0,
						allocatedAmount:
							option.defaultAmount ?? 0
					}));

		const existingAllocations: FormlyFieldConfig[] =
			[
				...this.allocationsRepeaterConfig
					.fieldGroup ?? []
			];

		while(existingAllocations.length > 0)
		{
			const item = existingAllocations.pop();
			this.allocationsRepeaterConfig
				?.props
				?.removeItem(item);
		}

		allocations
			.forEach(
				item => {
					this.allocationsRepeaterConfig
						?.props
						?.addItem(
							item);
				});
	}

	/**
	 * Handles the validity changed event for the dynamic formly component.
	 * This will update the validity of the wizard step for action buttons.
	 *
	 * @param {boolean} isValid
	 * The validity of the current displayed step data set.
	 * @memberof PaymentAllocationsComponent
	 */
	public dynamicFormlyValidityChanged(
		isValid: boolean): void
	{
		this.validityChanged
			.emit(
				isValid);
	}

	/**
	 * Sets the commission transaction associated
	 * with the commission payment. This also refreshes
	 * the controls that depend on the commission transaction data.
	 * @param {any} commission
	 * The commission transaction to set.
	 * @memberof PaymentAllocationsComponent
	 */
	public setCommissionTransaction(
		commission: any): void
	{
		this.commissionTransaction = commission;
		this.loadPaymentReferenceOptions();
		this.refreshAllocationsRepeater();
	}

	/**
	 * Sets the transaction amount in the parent component.
	 *
	 * @memberof PaymentAllocationsComponent
	 */
	private setTransactionAmount(): void
	{
		const transactionAmount: number =
			this.currentTransaction
				?.data
				?.accounting
				?.directWrittenTotal;

		this.transactionAmountField
			?.formControl
			?.setValue(
				transactionAmount);
		this.transactionAmountField
			?.formControl
			?.updateValueAndValidity();
	}

	/**
	 * Sets the invoice amount in the parent component
	 * based on the selected transaction.
	 *
	 * @memberof PaymentAllocationsComponent
	 */
	private setInvoiceAmount(): void
	{
		const invoiceAmount: number =
			ObjectHelper
				.getPropertyByJsonPath(
					this.currentTransaction,
					this.paymentAllocationSettings
						?.policy
						?.invoiceAmountPath) ?? 0;

		this.invoiceAmountField
			?.formControl
			?.setValue(
				Math.round(invoiceAmount * 100) / 100);
		this.invoiceAmountField
			?.formControl
			?.updateValueAndValidity();
	}

	/**
	 * Refreshes the payment allocations repeater to synchronize
	 * with the current transaction data.
	 *
	 * @memberof PaymentAllocationsComponent
	 */
	private refreshAllocationsRepeater(): void
	{
		this.allocationsRepeaterConfig
			.fieldGroup
			.forEach(
				(item: FormlyFieldConfig) =>
				{
					item.fieldGroup[1]
						.formControl
						.setValue(
							null);

					item.fieldGroup[0]
						.formControl
						.setValue(null);

					if (item.fieldGroup[0]
						.props
						.initializeDataOptions)
					{
						item.fieldGroup[0]
							.props
							.initializeDataOptions();
					}
				});
	}

	/**
	 * Synchronizes the allocation amount with the sum
	 * of the payment allocations.
	 *
	 * @memberof PaymentAllocationsComponent
	 */
	private synchronizeAllocationAmount(): void
	{
		const allocations: any[] =
			this.allocationsRepeaterConfig
				.fieldGroup
				.map(item => item.formControl.value);
		const allocatedAmount =
			allocations.reduce(
				(total, allocation) =>
					total
						+ (allocation.allocatedAmount
							?? 0),
				0);

		this.allocationAmountField
			?.formControl
			?.setValue(
				Math.round(allocatedAmount * 100) / 100);
		this.allocationAmountField
			?.formControl
			?.updateValueAndValidity();

		this._amountField
			?.formControl
			?.updateValueAndValidity();
	}

	/**
	 * Updates the transaction amount based on the selected reference option.
	 *
	 * @param {FormlyFieldConfig} field
	 * The formly field configuration for the reference option.
	 * @memberof PaymentAllocationsComponent
	 */
	private updateReferenceTransactionAmount(
		field: FormlyFieldConfig): void
	{
		const transactionAmountField =
			field
				.formControl
				.parent
				.get('transactionAmount');

		if (AnyHelper.isNull(
			field.formControl.value))
		{
			transactionAmountField
				.setValue(null);

			return;
		}

		const selectedOption =
			this.paymentReferenceOptions
				.find(
					option =>
						option.id === field.formControl.value);

		transactionAmountField
			.setValue(
				selectedOption?.defaultAmount ?? 0);
	}

	/**
	 * Refreshes the controls that depend on the current transaction.
	 *
	 * @memberof PaymentAllocationsComponent
	 */
	private refreshTransactionAssociatedControls(): void
	{
		this.loadPaymentReferenceOptions();
		this.setTransactionAmount();
		this.setInvoiceAmount();
		this.refreshAllocationsRepeater();
	}

	/**
	 * Decorates the auto allocate button with the
	 * auto allocate function and disabled state.
	 *
	 * @memberof PaymentAllocationsComponent
	 */
	private decorateAutoAllocateButton(): void
	{
		if (AnyHelper.isNull(
			this._autoAllocateButton))
		{
			return;
		}

		this._autoAllocateButton.props.operationPromise =
			'return async function(field) { '
				+ 'field.props.autoAllocate(); }';
		this._autoAllocateButton.props.autoAllocate =
			this.autoAllocate.bind(this);
		this._autoAllocateButton.expressions =
			{
				...this._autoAllocateButton.expressions,
				'props.disabled': () =>
					AnyHelper.isNull(
						this.currentTransaction)
						|| AnyHelper.isNull(
							this._amountField
								?.formControl
								?.value)
						|| this._amountField.formControl.value
							!== (this.paymentType
								=== InsuranceConstants
									.paymentAllocationTypes.commission
								? this.commissionTransaction?.data?.amount
								: this.invoiceAmountField.formControl.value)
			};
	}

	/**
	 * Decorates the amount field with a custom validator to ensure that
	 * the entered amount matches the sum of allocated amounts when
	 * payment allocations are enabled.
	 *
	 * @memberof PaymentAllocationsComponent
	 */
	private decorateAmountField(): void
	{
		this._amountField.validators =
			{
				...this._amountField.validators,
				amountMustMatchAllocationAmount: {
					expression: (control: AbstractControl) =>
						!this.paymentAllocationsEnabled
							|| AnyHelper.isNull(
								control.value)
							|| AnyHelper.isNull(
								this.allocationAmountField
									?.formControl
									?.value)
							|| control.value
								=== this.allocationAmountField
									.formControl
									.value,
					message: 'Amount must match allocation amount'
				}
			};
	}

	/**
	 * Validates the allocated amount.
	 *
	 * @param {number} value
	 * The allocated amount value.
	 * @returns {boolean}
	 * A boolean value indicating the amount validity.
	 * @member PaymentAllocationsComponent
	 */
	private validateAllocatedAmount(
		value: number): boolean
	{
		return value > 0
			|| (value < 0
				&& this.allowNegativeAmounts);
	}

	/**
	 * Creates the dynamic formly layout.
	 *
	 * @memberof PaymentAllocationsComponent
	 */
	private initializeLayout(): void
	{
		this.dynamicFormlyLayout =
			<FormlyFieldConfig[]>
			[
				{
					key: 'paymentAllocations',
					type: FormlyConstants.customControls.customRepeater,
					defaultValue: [],
					props: {
						label: 'Allocations',
						singular: 'Allocation',
						titleFunction:
							'return function(field){ '
								+ 'return (field.model?.referenceId '
								+ '? field.fieldGroup[0].props.options'
								+ '?.find(item => item.value === '
								+ 'field.model?.referenceId)?.label '
								+ ': "") + '
								+ '(field.fieldGroup[2].formControl.value '
								+ '? " - " + '
								+ 'field.props.context?.source.resolver'
								+ '.resolveStatic("StringHelper", "format", ['
								+ 'field.fieldGroup[2].formControl.value, '
								+ '"currency"]) '
								+ ': ""); }',
						gridColumns: 12,
						change:
							this.synchronizeAllocationAmount
								.bind(this),
						securityAccessPolicy: {
							rights: {
								create: true
							}
						}
					},
					validators: {
						minimumLength: {
							expression: (_control: AbstractControl) =>
								this.allocationsRepeaterConfig
									.fieldGroup
									.length > 0,
							message: 'At least one allocation is required.'
						},
						requiredFields: {
							expression: (_control: AbstractControl) =>
								this.allocationsRepeaterConfig
									.fieldGroup
									.every(
										item =>
											item.formControl?.valid
												?? false)
						}
					},
					fieldArray: {
						fieldGroup: [
							{
								key: 'referenceId',
								type:
									FormlyConstants
										.customControls
										.customDataSelect,
								wrappers: [
									FormlyConstants
										.customControls
										.customFieldWrapper
								],
								props: {
									label: 'Reference',
									placeholder: 'Select an Option',
									required: true,
									showClear: true,
									dataPromise:
										() => this.paymentReferenceOptions,
									labelTemplate: '${item.description}',
									valueTemplate: '${item.id}',
									change:
										this.updateReferenceTransactionAmount
											.bind(this)
								},
								expressions: {
									'props.label': () =>
										this.paymentAllocationSettings
											?.policy
											?.label || 'Reference'
								}
							},
							{
								key: 'transactionAmount',
								type:
									FormlyConstants
										.customControls
										.customInputNumber,
								wrappers: [
									FormlyConstants
										.customControls
										.customFieldWrapper
								],
								props: {
									label: 'Transaction Amount',
									useCurrency: true,
									disabled: true
								}
							},
							{
								key: 'allocatedAmount',
								type:
									FormlyConstants
										.customControls
										.customInputNumber,
								wrappers: [
									FormlyConstants
										.customControls
										.customFieldWrapper
								],
								props: {
									label: 'Amount',
									useCurrency: true,
									required: true,
									change:
										this.synchronizeAllocationAmount
											.bind(this)
								},
								validators: {
									amountMustBeGreaterThanZero: {
										expression:
											(control: AbstractControl) =>
												this.validateAllocatedAmount(
													control.value),
										message:
											'Amount must be greater than zero'
									}
								}
							}
						]
					}
				}
			];
	}
}
